import { useCallback, useEffect, useRef } from "react";
import { toast } from "@/components/ui/sonner";
import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { flushOfflineQueue } from "@/lib/db/mutation-handlers";

const FLUSH_DEBOUNCE_MS = 1000;

/**
 * Sole owner of service worker registration for flowstate-start.
 * Prompts the user to refresh when a new version is waiting so the installed
 * PWA updates in place instead of requiring a reinstall. Also flushes the
 * offline mutation queue when connectivity returns.
 */
export function PWAUpdateNotifier() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const flushTimeoutRef = useRef<number | null>(null);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current !== null) {
      window.clearTimeout(flushTimeoutRef.current);
    }
    flushTimeoutRef.current = window.setTimeout(() => {
      flushTimeoutRef.current = null;
      void flushOfflineQueue();
    }, FLUSH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const swUrl =
      typeof __APP_VERSION__ !== "undefined"
        ? `/sw.js?v=${__APP_VERSION__}`
        : "/sw.js";

    let cancelled = false;
    let removeUpdateFound: (() => void) | undefined;

    navigator.serviceWorker
      .register(swUrl, { updateViaCache: "none" })
      .then((reg) => {
        if (cancelled) return;
        registrationRef.current = reg;
        void reg.update();

        const onWaiting = () => {
          logger.info("PWA: new service worker waiting");
          toast.info("Update available", {
            description: "Refresh to get the latest version.",
            action: {
              label: "Refresh",
              onClick: () => {
                if (reg.waiting) {
                  try {
                    reg.waiting.postMessage({ type: "SKIP_WAITING" });
                  } catch (err) {
                    logger.warn("PWA: failed to send SKIP_WAITING", {
                      error: err,
                    });
                    window.location.reload();
                  }
                }
              },
            },
          });
        };

        const onUpdateFound = () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          const stateChangeHandler = () => {
            if (
              newWorker.state === "installed" &&
              (navigator.serviceWorker.controller || reg.active)
            ) {
              onWaiting();
            }
          };

          newWorker.addEventListener("statechange", stateChangeHandler);
        };

        if (reg.waiting) onWaiting();
        reg.addEventListener("updatefound", onUpdateFound);
        removeUpdateFound = () => {
          reg.removeEventListener("updatefound", onUpdateFound);
        };
      })
      .catch((err: unknown) =>
        logger.warn("PWA: service worker registration failed", {
          error: err instanceof Error ? err.message : String(err),
        })
      );

    const onControllerChange = () => {
      window.location.reload();
    };

    const onOnline = () => {
      scheduleFlush();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleFlush();
      }
    };

    const onServiceWorkerMessage = (event: MessageEvent) => {
      const message = event.data as { type?: string } | undefined;
      if (message?.type === "flush-offline-queue") {
        scheduleFlush();
      }
    };

    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "INITIAL_SESSION"
      ) {
        scheduleFlush();
      }
    });

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange
    );
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);
    navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);

    scheduleFlush();

    return () => {
      cancelled = true;
      removeUpdateFound?.();
      authSubscription.unsubscribe();
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange
      );
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      navigator.serviceWorker.removeEventListener(
        "message",
        onServiceWorkerMessage
      );
      if (flushTimeoutRef.current !== null) {
        window.clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
    };
  }, [scheduleFlush]);

  return null;
}
